import { Button } from '@/components/elements/forms/buttons';
import { _t } from '@/locale';
import WeekHeader from './components/WeekHeader/WeekHeader';
import { PickTimeNoTimesAtAllProps } from './PickTimeNoTimesAtAll.types';

/**
 * "No times at all" pick-time content view (MWA-64, spec FR-009/FR-019).
 *
 * Spec: marketplace-specs/specs/002-MPA-2598-weekpicker (reviewed commit and Figma nodes recorded in the PR)
 *
 * The empty state shown when a service/employee has no availability across the whole booking
 * horizon (`firstAvailableDate == null`) — not merely the visible week. The visible-week
 * "no times in frame" case is the week view's (MPA-2583) empty-week jump-to-next-available;
 * the standalone no-times-in-frame banner was dropped for v1 (spec decisions.md, 2026-06-22).
 * Recovery actions follow FR-019: the waitlist and change-employee links
 * render only when their handler is supplied, so the caller can withhold the waitlist in the
 * sistaminuten/change-time/deal flows and the change-employee link when no employee filter applies.
 *
 * Presentational only — the pick-time container wires the handlers and rollout flag (MPA-2581+).
 */
const PickTimeNoTimesAtAll = ({
  title,
  subtitle,
  isNarrow = false,
  onChooseAnotherService,
  onJoinWaitlist,
  onChangeEmployee,
  regularTimesUrl,
}: PickTimeNoTimesAtAllProps) => {
  const body = (
    <div className="gap-3xl px-xl py-3xl flex h-full w-full flex-col items-center justify-center">
      <div className="gap-md flex w-full flex-col text-center" role="status" aria-live="polite">
        <h3 className="text-h-xl text-fg_primary font-bold">{_t('features.weekpicker.PickTimeNoTimesAtAll.title')}</h3>
        <p className="text-l text-fg_secondary">{_t('features.weekpicker.PickTimeNoTimesAtAll.body')}</p>
        {regularTimesUrl && (
          <p className="text-l text-fg_secondary">
            {_t('notAvailableSistaminutentider')}
            <a href={regularTimesUrl} target="_blank" rel="noreferrer" className="underline">
              Bokadirekt
            </a>
          </p>
        )}
      </div>
      {(onChooseAnotherService || onJoinWaitlist || onChangeEmployee) && (
        <div className="gap-sm flex flex-col items-center">
          {onChooseAnotherService && (
            <Button variant="primary" size="md" onClick={onChooseAnotherService}>
              {_t('features.weekpicker.PickTimeNoTimesAtAll.chooseAnotherService')}
            </Button>
          )}
          {(onJoinWaitlist || onChangeEmployee) && (
            <div className="flex flex-col items-center">
              {onJoinWaitlist && (
                <Button variant="link" size="md" onClick={onJoinWaitlist}>
                  {_t('features.weekpicker.PickTimeNoTimesAtAll.joinWaitlist')}
                </Button>
              )}
              {onChangeEmployee && (
                <Button variant="link" size="md" onClick={onChangeEmployee}>
                  {_t('features.weekpicker.PickTimeNoTimesAtAll.changeEmployee')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const header = <WeekHeader title={title} subtitle={subtitle} isNarrow={isNarrow} prevDisabled nextDisabled />;

  if (isNarrow) {
    return (
      <div className="border-border_regular flex h-full w-full flex-col border-b">
        {header}
        <div className="border-border_regular bg-surface_l2 flex-1 border-t">{body}</div>
      </div>
    );
  }

  return (
    <div className="border-border_regular bg-surface_l2 flex h-full w-full flex-col overflow-hidden rounded-lg border">
      {header}
      <div className="flex-1">{body}</div>
    </div>
  );
};

export default PickTimeNoTimesAtAll;
